import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { fetchLoanApplicationsDashboard } from "../../../api/loanApplications";
import { createRestructuring, fetchRestructuringPreview } from "../../../api/restructuring";
import { formatDate, formatTZS } from "../../../utils/format";
import { runCrud } from "../Common/credoraToast";

const ACTIVE_STATUSES = new Set(["disbursed", "active"]);

const NewRestructuringModal = ({ isOpen, toggle, onCreated, initialLoanId = "" }) => {
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [loanId, setLoanId] = useState("");
  const [tenureMonths, setTenureMonths] = useState("12");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLoanId(initialLoanId ? String(initialLoanId) : "");
    setTenureMonths("12");
    setReason("");
    setNotes("");
    setPreview(null);
    setError("");
    setLoadingLoans(true);
    fetchLoanApplicationsDashboard()
      .then((data) => {
        const rows = (data?.records ?? []).filter((row) => ACTIVE_STATUSES.has(row.raw_status));
        setLoans(rows);
      })
      .catch(() => setError("Failed to load active loans."))
      .finally(() => setLoadingLoans(false));
  }, [isOpen, initialLoanId]);

  const loanOptions = useMemo(
    () =>
      loans.map((row) => ({
        value: row.loan_id,
        label: `${row.application_id} · ${row.borrower_name}`,
        meta: `${row.loan_type} · ${formatTZS(row.amount)}`,
        row,
      })),
    [loans]
  );

  const selectedLoan = loanOptions.find((opt) => opt.value === loanId)?.row;

  useEffect(() => {
    if (!loanId || !tenureMonths || Number(tenureMonths) < 1) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    fetchRestructuringPreview(loanId, { proposed_tenure_months: Number(tenureMonths) })
      .then(setPreview)
      .catch(() => setPreview(null))
      .finally(() => setLoadingPreview(false));
  }, [loanId, tenureMonths]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loanId) {
      setError("Select a loan to restructure.");
      return;
    }
    if (!reason.trim()) {
      setError("Enter a reason for restructuring.");
      return;
    }
    if (!tenureMonths || Number(tenureMonths) < 1) {
      setError("Enter a valid proposed tenure.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await runCrud(
        () =>
          createRestructuring({
            loan_id: loanId,
            reason: reason.trim(),
            notes: notes.trim() || null,
            proposed_tenure_months: Number(tenureMonths),
          }),
        { successMessage: "Restructuring request submitted." }
      );
      onCreated?.();
      toggle();
    } catch {
      /* toast handled */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Request loan restructuring</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error ? <Alert color="danger">{error}</Alert> : null}
          <FormGroup>
            <Label>Loan <span className="text-danger">*</span></Label>
            {loadingLoans ? (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner size="sm" /> Loading active loans…
              </div>
            ) : (
              <Select
                options={loanOptions}
                value={loanOptions.find((opt) => opt.value === loanId) || null}
                onChange={(opt) => setLoanId(opt ? opt.value : "")}
                placeholder="Select active loan…"
                isClearable
                formatOptionLabel={(opt) => (
                  <div>
                    <div>{opt.label}</div>
                    <small className="text-muted">{opt.meta}</small>
                  </div>
                )}
              />
            )}
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>New tenure (months) <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  max="360"
                  value={tenureMonths}
                  onChange={(e) => setTenureMonths(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Outstanding principal</Label>
                <Input
                  value={preview ? formatTZS(preview.outstanding_principal) : "—"}
                  disabled
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Reason <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows="3"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Borrower income reduced — extend tenure to lower EMI"
            />
          </FormGroup>
          <FormGroup>
            <Label>Notes (optional)</Label>
            <Input type="textarea" rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormGroup>
          {loadingPreview ? (
            <div className="credora-restructuring-preview credora-restructuring-preview--loading">
              Calculating preview…
            </div>
          ) : preview ? (
            <div className="credora-restructuring-preview">
              <div className="credora-restructuring-preview__title">Preview</div>
              <div className="credora-restructuring-preview__grid">
                <div className="credora-restructuring-preview__item">
                  <span className="credora-restructuring-preview__label">Fee</span>
                  <span className="credora-restructuring-preview__value">{formatTZS(preview.restructuring_fee)}</span>
                </div>
                <div className="credora-restructuring-preview__item">
                  <span className="credora-restructuring-preview__label">Effective</span>
                  <span className="credora-restructuring-preview__value">{formatDate(preview.effective_date)}</span>
                </div>
                <div className="credora-restructuring-preview__item">
                  <span className="credora-restructuring-preview__label">New installments</span>
                  <span className="credora-restructuring-preview__value">{preview.proposed_schedule?.length || 0}</span>
                </div>
              </div>
              {selectedLoan ? (
                <div className="credora-restructuring-preview__note">
                  Replacing {preview.previous_installments_remaining} remaining installment(s) on{" "}
                  {selectedLoan.application_id}.
                </div>
              ) : null}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggle} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={saving || loadingLoans}>
            {saving ? <Spinner size="sm" /> : "Submit request"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default NewRestructuringModal;
